import Warning from './icons/health-warning.svg';
import Critical from './icons/health-critical.svg';
import Normal from './icons/health-normal.svg';
import Unknown from './icons/health-unknown.svg';

export enum HealthStatus {
  NORMAL = 0,
  UNKNOWN = 1,
  WARNING = 2,
  CRITICAL = 3,
}

interface HealthStatusDef {
  status: HealthStatus;
  name: string;
  color: string;
}

// NOTE: color here is the value of the color, do not replace it with tailwind className
// we will use this value to pass it to HealthBar, Scatter plot etc which don't understand tailwind classNames
const HEALTH_STATUS_DEFS: HealthStatusDef[] = [
  { status: HealthStatus.NORMAL, name: 'Normal', color: 'var(--color-positive-bg)' },
  { status: HealthStatus.UNKNOWN, name: 'Unknown', color: 'var(--color-dormant-bg)' },
  { status: HealthStatus.WARNING, name: 'Warning', color: 'var(--color-warning-bg)' },
  { status: HealthStatus.CRITICAL, name: 'Critical', color: 'var(--color-negative-bg)' },
];

// - For tailwind to pickup these custom classNames
// - We are creating these classNames to match the health colors defined abouve and
//   these health colors classNames can be used by templates.
// - If following classNames are replaced by other tailwind classNames,
//   make sure above HEALTH_STATUS_DEFS:color get the value color value but don't replace it with tailwind className
const tailwindClassNames = ['positive-bg', 'dormant-bg', 'warning-bg', 'negative-bg)'];

export const getHealthStatusDef = (status: HealthStatus): HealthStatusDef => {
  const def = HEALTH_STATUS_DEFS.filter(hdef => hdef.status === status);
  return def && def.length > 0 ? def[0] : HEALTH_STATUS_DEFS[HealthStatus.UNKNOWN];
};

export const statusToHealthColor = (status: HealthStatus) => getHealthStatusDef(status).color;

export const HEALTHCSSCLASS = {
  [HealthStatus.NORMAL]: 'health-normal',
  [HealthStatus.UNKNOWN]: 'health-unknown',
  [HealthStatus.WARNING]: 'health-warning',
  [HealthStatus.CRITICAL]: 'health-critical',
} as const;

// returns tailwind className (convert from magnetic color value)
export const statusToHealthClassName = (status: HealthStatus) => {
  const magneticColor = statusToHealthColor(status);
  return 'text-' + magneticColor.substring(12, magneticColor.length - 1);
};

export const statusToText = (status: HealthStatus) => getHealthStatusDef(status).name;

export const textToStatus = (text: string) => {
  const def = HEALTH_STATUS_DEFS.filter(hdef => hdef.name.toLocaleLowerCase() === text.toLocaleLowerCase());
  return def && def.length > 0 ? def[0] : HEALTH_STATUS_DEFS[HealthStatus.UNKNOWN];
};

export const HealthIcon = {
  [HealthStatus.NORMAL]: 'Health.Normal',
  [HealthStatus.UNKNOWN]: 'Status.Unknown',
  [HealthStatus.WARNING]: 'Health.Warning',
  [HealthStatus.CRITICAL]: 'Health.Critical',
};

export const statusToHealthIcon = (status: HealthStatus) => HealthIcon[status] ?? 'Status.Unknown';

export const statusToHealthLabel = (v: number) => {
  switch (v) {
    case 0:
      return 'Normal';
      break;
    case 2:
      return 'Warning';
      break;
    case 3:
      return 'Critical';
      break;
    default:
      return 'Unknown';
  }
};

// getHealthStatusCount extracts the total count for a given health status from the UQL query result
// UQL query result has to be in the format [{status, count}, ...]
interface HealthData {
  status: HealthStatus;
  count: number;
}

export const getHealthStatusCount = (data: HealthData[], status: HealthStatus) => {
  let totalCount = 0;
  let found = false;

  data &&
    data.forEach(curr => {
      if ((status === 1 && (curr.status === 1 || curr.status === null)) || curr.status === status) {
        totalCount += curr.count;
        found = true;
      }
    });

  return found ? totalCount : undefined;
};

// getStateCount extracts the total count for a given 'state' from the UQL query result
// UQL query result has to be in the format [{state, count}, ...]
interface StateData {
  state: string;
  count: number;
}

export const getStateCount = (data: StateData[], state: string) => {
  let totalCount = 0;
  let found = false;

  data &&
    data.forEach(curr => {
      if (
        (state.toLocaleLowerCase() === 'unknown' && curr.state === null) ||
        (curr.state !== null && curr.state.toLocaleLowerCase() === state.toLocaleLowerCase())
      ) {
        totalCount += curr.count;
        found = true;
      }
    });

  return found ? totalCount : undefined;
};
